import $ from 'jquery'
import { useClickOutside } from 'stimulus-use'
import { Controller } from 'stimulus'
import tippy from 'tippy.js'
import { enter, leave } from 'el-transition'
import {
  registerEscLayer,
  unregisterEscLayer,
} from '~/common/helpers/esc_stack'
import { focusableElements, isFocusable } from '~/common/helpers/focus_trap'
import { reconcileHiddenAfterLeaveSettles } from './dropdown_hidden_reconcile'
import './dropdown_component.css'

export default class extends Controller {
  static targets = ['button', 'lazyContent', 'content', 'menu']
  static values = {
    placement: String,
    autoflip: Boolean,
    fallbackPlacements: Array,
    minWidth: String,
    offsetDistance: Array,
    referenceSelector: String,
    useTransforms: Boolean,
    startOpen: Boolean,
    autoFocusOnMobile: Boolean,
    maxWidth: String,
    appendToBody: Boolean,
    appendToParentSelector: String,
    closeOnClick: Boolean,
    strategy: String,
    resetOnOpen: Boolean,
    mobileContentId: String,
  }

  connect() {
    // There are places where we render so many things that eagerly rendering stuff has huge perf hit
    // One such example is on builder's style guide. Without this we'd render 1000s of things eagerly. Which is noticeably slow
    // On those place, we pass lazy: true, which renders the content as a <template></template>
    // And we only set it up when it's required (e.g user opens the dropdown)
    if (!this.hasLazyContentTarget) {
      if (this._hydratedLazyMenu) this.observeLazyMenu(this.activeMenu)
      this.setup()
    }

    // A remote-content menu (DropdownComponent content_url:) isn't in the page: fetch it in the
    // background once the page is idle, so it's ready before anyone opens it. With
    // prefetch_content: false it is fetched on the first open instead (openMenu).
    if (
      this.hasLazyContentTarget &&
      this.lazyContentTarget.dataset?.contentUrl &&
      this.lazyContentTarget.dataset.prefetch !== 'false'
    ) {
      const prefetch = () => this.loadRemoteContent()
      if (window.requestIdleCallback)
        window.requestIdleCallback(prefetch, { timeout: 3000 })
      else setTimeout(prefetch, 1000)
    }

    // The bottom sheet is a sibling of this controller, so its before-open event
    // cannot reach a Stimulus action on the dropdown root. Hydrate synchronously
    // here, before the sheet mounts and moves its content into the portal.
    const mobileSheet = this.element.nextElementSibling
    if (mobileSheet?.dataset.dropdownMobileSheet !== undefined) {
      this._mobileSheet = mobileSheet
      this._onMobileBeforeOpen = () => {
        this.cancelPendingLazyOpen()
        const fill = () => {
          this.setupIfLazy()
          this.moveContentToMobile()
        }
        // A remote menu still loading: open the sheet now, fill it when it arrives.
        if (
          this.hasLazyContentTarget &&
          this.lazyContentTarget.dataset?.contentUrl
        )
          this.loadRemoteContent().then((loaded) => loaded && fill())
        else fill()
      }
      mobileSheet.addEventListener(
        'bottom-sheet:before-open',
        this._onMobileBeforeOpen
      )
    }
  }

  // The menu grows out of its trigger (dropdown_component.css). The placement gives a corner,
  // but a menu shifted to fit the screen (a long one on a phone) can sit far from that corner
  // or over the trigger, so the origin is the trigger's center, kept inside the menu. enter()
  // has just shown the menu (still at its starting scale) and the scale-up starts two frames
  // later, so position it now and measure: layout sizes ignore the scale.
  growFromTrigger() {
    const popper = this.__content
    const tippy = popper?.tippy
    if (!tippy) return
    tippy.popperInstance?.forceUpdate()
    const menu = popper.getBoundingClientRect()
    if (!menu.width || !menu.height) return
    const trigger = (
      tippy.props.getReferenceClientRect ||
      (() => tippy.reference.getBoundingClientRect())
    )()
    const inside = (value, size) =>
      Math.round(Math.min(Math.max(value, 0), size))
    const x = inside(trigger.left + trigger.width / 2 - menu.left, menu.width)
    const y = inside(trigger.top + trigger.height / 2 - menu.top, menu.height)
    popper.target.style.transformOrigin = `${x}px ${y}px`
  }

  setup() {
    // We have a check here and one below in disconnect, because a dropdown component moves things outside of the container element
    // And there are somethings like draggables that clone the node, and when it does, since the content is not here, it won't clone that, so the cloned element won't have the menu target which would raise.
    if (!this.hasContentTarget) return

    let getReferenceClientRect
    if (this.referenceSelectorValue) {
      this.referenceEl =
        this.element.closest(this.referenceSelectorValue) ||
        document.querySelector(this.referenceSelectorValue)
      if (this.referenceEl) {
        getReferenceClientRect = () => this.referenceEl.getBoundingClientRect()
      }
    }

    // Inside a region marked data-dropdown-boundary, the menu flips and shifts to stay in that
    // region, not just in the window: something can cover the rest (the Coach Mac app's native
    // composer, over the bottom of the conversation).
    const boundary =
      this.element.closest('[data-dropdown-boundary]') || 'clippingParents'

    const tippyInstance = tippy(this.buttonTarget, {
      getReferenceClientRect,
      content: this.contentTarget,
      trigger: 'manual',
      hideOnClick: false,
      placement: this.placementValue,
      maxWidth: this.hasMaxWidthValue ? this.maxWidthValue : 'none',
      interactive: true,
      detachOnUnmount: false,
      // The content is already role="menu". Tippy's default role="tooltip" would announce the
      // menu as a tooltip, and the hidden popper stays in the DOM, matching [role=tooltip] on every page.
      role: null,
      offset: this.offsetDistanceValue, // [offsetSkidding, offsetDistance]
      onMount: (instance) => {
        $(instance.popper).changed() // So things like tooltips work correctly.
      },
      onShow: (instance) => {
        instance.popper.inert = false
        // Let opted-in content (e.g. the add-step popover) reset itself before it
        // becomes visible, so reopening never starts in a left-over state.
        if (this.resetOnOpenValue) {
          instance.popper
            .querySelectorAll('[data-action*="dropdown:reset"]')
            .forEach((el) =>
              el.dispatchEvent(new CustomEvent('dropdown:reset'))
            )
        }
        instance.popper.style.display = ''
        this.element.classList.add('dropdown-open')
      },
      // Tippy's hideAll() (e.g. showModal) hides this without closeMenu. A
      // hidden dropdown must not keep owning Escape.
      onHide: () => {
        unregisterEscLayer('dropdown', this.#escLayerClose)
      },
      onHidden: (instance) => {
        instance.popper.style.display = 'none'
        this.element.classList.remove('dropdown-open')
      },

      // Let's try fixed strategy provided by tippy to see if it causes problem. There's a whole bunch of code here that was written because we used the appendToStrategy which moves the elements outside the controller's main element thus requiring us to manually manage it (i.e add event listeners, etc.) instead of using stimulus stuff.
      // If the fixed strategy works for all cases, I think we just use that (let's wait a few months to make sure before deleting code?), then remove the ugly code that was created to manage the appendToStrategy and we're good to go.
      popperOptions: {
        strategy: this.strategyValue || 'fixed',
        modifiers: [
          {
            name: 'flip',
            enabled: this.hasAutoflipValue ? this.autoflipValue : true,
            options: {
              boundary,
              fallbackPlacements: this.hasFallbackPlacementsValue
                ? this.fallbackPlacementsValue
                : undefined,
            },
          },
          {
            name: 'preventOverflow',
            options: {
              boundary,
              padding: 2,
              mainAxis: true,
              altAxis: true,
            },
          },
          {
            name: 'computeStyles',
            options: {
              gpuAcceleration: this.useTransformsValue,
            },
          },
        ],
      },
      appendTo: this.resolveAppendTo(),
    })

    this.__content = tippyInstance.popper
    this.__content.classList.add('tw')
    // Deliberately no `dark` class on the popper: it stays within the document, so it inherits
    // the page's dark-mode scope from <html>. A local `dark` class here goes stale on a theme
    // switch, and re-fires nested-`.dark` CSS rules (e.g. the style guide's `--tw-base-font-size`)
    // in the wrong context — which shifted the dropdown's font size between light and dark.
    this.__content.querySelector('.tippy-content').classList.add('p-0')
    this.__content.target =
      this.__content.querySelector('.tippy-content').children[0]
    this.__content.tippy = tippyInstance
    // The popper is appended outside this element, so the lazy content's `loaded` event in
    // dropdown_component.rb never reaches it. Reposition when the menu's real content arrives:
    // a menu opened at its placeholder height may need to flip up once it is full size.
    this.__content.addEventListener('lazy-content:loaded', () =>
      this.reposition()
    )
    this.updateContentMinWidth()
    // onlyVisible: false — stimulus-use's default (true) skips outside-click handling when
    // the trigger element isn't in the viewport. Portaled builder control dropdowns (the
    // 'absolute' strategy) are anchored to a portaled trigger positioned over their node,
    // which is frequently off-screen (edge nodes / below the fold), so the default left them
    // impossible to dismiss by clicking outside. Whether an OPEN popover should close on an
    // outside click is independent of the trigger's visibility. Scoped to the 'absolute'
    // strategy so every other dropdown keeps the default app-wide behavior.
    useClickOutside(this, {
      element: this.element,
      onlyVisible: this.strategyValue !== 'absolute',
    })

    // Tippy moves the content out of this.element, so the `click->dropdown#closeMenu`
    // data-action inside the content can no longer find this controller via Stimulus.
    // Bind the close-on-click handler directly on the popper instead.
    if (this.closeOnClickValue) {
      this._onPopperClick = (e) => this.closeMenu(e)
      this.__content.addEventListener('click', this._onPopperClick)
    }

    this.__content.addEventListener('keydown', this.tabOutOfPopover)

    // Hack to hide & show tippy so the content gets reconnected to the DOM.
    // This is useful in case we want to dispatch events to things in the content but we are not sure if it will be opened first.
    // AFAIK shouldn't have any side effects (i.e no flickers).
    tippyInstance.show()

    if (this.startOpenValue) {
      this.dispatch('shown')
      // start_open bypasses openMenu() entirely, so it needs its own registration — otherwise a
      // dropdown that starts open is invisible to the Esc stack, and Escape could reach past it
      // to close a lower-priority layer underneath instead. ENG-33816.
      registerEscLayer('dropdown', this.#escLayerClose)
    } else {
      tippyInstance.hide()
      this.element.classList.remove('dropdown-open') // Sometimes this won't call the onHidden hook, (I think if the dropdown is in a hidden container?)
    }
  }

  // Resolves true once the menu's content is in the page (right away for a normal lazy
  // menu), false if a remote menu couldn't be fetched. One request per dropdown.
  loadRemoteContent() {
    const template = this.hasLazyContentTarget ? this.lazyContentTarget : null
    if (!template?.dataset?.contentUrl) return Promise.resolve(true)

    this._remoteContent ??= fetch(template.dataset.contentUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((response) => {
        if (!response.ok || response.redirected)
          throw new Error(`HTTP ${response.status}`)
        return response.text()
      })
      .then((html) => {
        // The server renders one cached copy of the menu for everyone, with placeholders
        // for the ids this dropdown generated.
        template.innerHTML = html
          .replaceAll(
            '__dropdown_desktop_id__',
            template.dataset.desktopContentId
          )
          .replaceAll('__dropdown_mobile_id__', this.mobileContentIdValue || '')
        delete template.dataset.contentUrl
        // A programmatic pick may have landed while this was in flight. The select
        // moves its checkmark onto that value now that the options exist.
        this.dispatch('remote-loaded')
        return true
      })
      .catch(() => {
        this._remoteContent = null // the next open tries again
        return false
      })
    return this._remoteContent
  }

  setupIfLazy() {
    // Not fetched yet: hydrating now would set up an empty menu.
    if (this.hasLazyContentTarget && this.lazyContentTarget.dataset?.contentUrl)
      return
    if (this.hasLazyContentTarget) {
      this.lazyContentTarget.outerHTML = this.lazyContentTarget.innerHTML
      this._hydratedLazyMenu = true
      this.observeLazyMenu(this.activeMenu)
      if (this._mobileSheet) {
        const mobileSelector = `#${this.mobileContentIdValue}`
        this._mobileElements = [
          ...this.element.querySelectorAll('[data-controller~="move-element"]'),
        ]
          .filter(
            (element) =>
              element.dataset.moveElementMobileContainerSelectorValue ===
              mobileSelector
          )
          .map((element) => ({ element, home: element.parentElement }))
      }
      this.setup()
    }
  }

  observeLazyMenu(menu) {
    this._lazyMenu?.removeEventListener(
      'menu:connected',
      this._onLazyMenuConnected
    )
    this._lazyMenu?.removeEventListener(
      'menu:disconnected',
      this._onLazyMenuDisconnected
    )
    this._lazyMenu = menu
    if (!menu) return

    this._lazyMenuReady =
      !!this.application?.getControllerForElementAndIdentifier(menu, 'menu')
    this._onLazyMenuConnected = (event) => {
      if (event.target !== menu) return
      this._lazyMenuReady = true
      this.flushPendingMenuAction()
    }
    this._onLazyMenuDisconnected = (event) => {
      if (event.target === menu) this._lazyMenuReady = false
    }
    menu.addEventListener('menu:connected', this._onLazyMenuConnected)
    menu.addEventListener('menu:disconnected', this._onLazyMenuDisconnected)
  }

  moveContentToMobile() {
    const mobileContent = this._mobileSheet?.querySelector(
      `#${this.mobileContentIdValue}`
    )
    if (!mobileContent) return
    this._mobileElements?.forEach(({ element }) => {
      if (element.parentElement !== mobileContent) mobileContent.append(element)
    })
  }

  cancelPendingLazyOpen() {
    this._lazyOpenGeneration = (this._lazyOpenGeneration || 0) + 1
    if (this._lazyOpening && this.hasButtonTarget)
      this.buttonTarget.classList.remove('is-dropdown-loading')
    this._lazyOpening = false
    this._pendingMenuEvent = null
    this._pendingSearchKey = null
    this._needsAutoActivate = false
  }

  flushPendingMenuAction() {
    if (!this.__content?.tippy || this.menuIsClosed()) return
    if (
      !this._needsAutoActivate &&
      !this._pendingMenuEvent &&
      !this._pendingSearchKey
    )
      return
    const menu = this.activeMenu || this._lazyMenu
    if (!menu) {
      this._pendingMenuEvent = null
      this._pendingSearchKey = null
      return
    }

    if (this._needsAutoActivate) {
      this.dispatch('menu:autoActivateItem', { prefix: false, target: menu })
      this._needsAutoActivate = false
    }
    if (this._pendingMenuEvent) {
      this.dispatch(this._pendingMenuEvent, { prefix: false, target: menu })
      this._pendingMenuEvent = null
    }
    if (this._pendingSearchKey) {
      this.dispatch('menu:searchKeypress', {
        prefix: false,
        target: menu,
        detail: { key: this._pendingSearchKey },
      })
      this._pendingSearchKey = null
    }
  }

  resolveAppendTo() {
    if (this.appendToParentSelectorValue) {
      return () =>
        this.buttonTarget.closest(this.appendToParentSelectorValue) ||
        document.querySelector('.tippy-portal') ||
        document.body
    }
    if (this.appendToBodyValue) {
      return () => document.querySelector('.tippy-portal') || document.body
    }
    return undefined
  }

  // A stable reference so register/unregister always target the same callback — see
  // esc_stack.js. Let the contents handle Escape before closing the dropdown;
  // a link picker uses it to return to the previous pane. Lower layers still defer.
  #escLayerClose = () => this.onEscape()

  disconnect() {
    unregisterEscLayer('dropdown', this.#escLayerClose)
    this.cancelPendingLazyOpen()
    clearTimeout(this.closeMenuOnMouseLeaveTimeout)
    this.closeMenuOnMouseLeaveTimeout = null
    this._lazyMenu?.removeEventListener(
      'menu:connected',
      this._onLazyMenuConnected
    )
    this._lazyMenu?.removeEventListener(
      'menu:disconnected',
      this._onLazyMenuDisconnected
    )
    this._lazyMenu = null
    this._lazyMenuReady = false
    this._mobileSheet?.removeEventListener(
      'bottom-sheet:before-open',
      this._onMobileBeforeOpen
    )
    this._mobileSheet = null
    this._mobileElements?.forEach(({ element, home }) => {
      if (element.parentElement !== home) home.append(element)
    })
    if (!this.__content) return
    if (this._onPopperClick)
      this.__content.removeEventListener('click', this._onPopperClick)
    this.__content.removeEventListener('keydown', this.tabOutOfPopover)
    this.element.appendChild(this.__content.target)
    this.__content.tippy.destroy()
    this.__content.remove()
    this.__content = null
    this._onPopperClick = null
  }

  // Tab never gets stuck in an open dropdown. The popover counts as part of its trigger: Tab from
  // a menu (a picker's search box, its items), or off either end of the popover, closes it and
  // moves on from the trigger to the next (or, with Shift, the previous) field. A control after
  // the menu, like the Add button in a picker's footer, is a stop on the way.
  tabOutOfPopover = (e) => {
    if (e.key !== 'Tab' || e.defaultPrevented) return
    if (e.ctrlKey || e.altKey || e.metaKey || this.menuIsClosed()) return

    const inPopover = focusableElements(this.__content)
    const edge = e.shiftKey ? inPopover[0] : inPopover.at(-1)
    const inMenu = e.target.closest?.('[data-controller~="menu"]')
    if (!inMenu && e.target !== edge) return

    if (inMenu) {
      const menuStops = inPopover.map((el) => inMenu.contains(el))
      const i = e.shiftKey
        ? menuStops.indexOf(true)
        : menuStops.lastIndexOf(true)
      const next = i >= 0 && inPopover[e.shiftKey ? i - 1 : i + 1]
      if (next) {
        e.preventDefault()
        next.focus()
        return
      }
    }

    const from = this.#triggerFocusable({ first: e.shiftKey })
    if (!from) return
    this.closeMenu()
    // The browser's own Tab then moves on from here
    this.#focusTrigger(from)
  }

  #triggerFocusable({ first = false } = {}) {
    // The popover can be appended inside the trigger
    const focusables = focusableElements(this.buttonTarget).filter(
      (el) => !this.__content?.contains(el)
    )
    if (
      !focusables.length &&
      isFocusable(this.buttonTarget) &&
      this.buttonTarget.tabIndex >= 0
    )
      focusables.push(this.buttonTarget)
    return first ? focusables[0] : focusables.at(-1)
  }

  // Without opening it again (open_on_focus)
  #focusTrigger(el = this.#triggerFocusable()) {
    if (!el) return
    this._suppressFocusOpenUntil = Date.now() + 200
    el.focus()
  }

  /* ====== Public API ====== */
  // `dropdown:focus` from a field inside the trigger: focus it without opening the menu (open_on_focus)
  focusWithoutOpening(e) {
    if (this.buttonTarget.contains(e.target)) this.#focusTrigger(e.target)
  }

  onEscape() {
    if (!this.dispatch('escape', { cancelable: true }).defaultPrevented)
      this.closeMenu()
  }

  onArrowDown(e) {
    e.preventDefault()
    if (this.menuIsClosed()) {
      this._pendingMenuEvent = 'menu:arrowDown'
      this.openMenu(e)
      return
    }
    this.dispatchEventToActiveMenu('menu:arrowDown', e)
  }

  onArrowUp(e) {
    e.preventDefault()
    if (this.menuIsClosed()) {
      this._pendingMenuEvent = 'menu:arrowUp'
      this.openMenu(e)
      return
    }
    this.dispatchEventToActiveMenu('menu:arrowUp', e)
  }

  onArrowRight(e) {
    e.preventDefault()
    this.dispatchEventToActiveMenu('menu:arrowRight', e)
  }

  onArrowLeft(e) {
    e.preventDefault()
    this.dispatchEventToActiveMenu('menu:arrowLeft', e)
  }

  onEnterKey(e) {
    e.preventDefault()
    // Mirrors onArrowUp/onArrowDown above: on a closed (e.g. lazy, never-yet-opened) dropdown,
    // activeMenu can be null or have no active item, so dispatching straight to it is a silent
    // no-op — Enter must open the menu first, per the "hitting space or enter ... to open"
    // keyboard contract (ENG-33787).
    if (this.menuIsClosed()) {
      this.openMenu(e)
      return
    }
    this.dispatchEventToActiveMenu('menu:enterKey', e)
  }

  onKeydown(e) {
    // Skip keys handled by specific actions
    if (
      [
        'ArrowUp',
        'ArrowDown',
        'ArrowLeft',
        'ArrowRight',
        'Enter',
        'Escape',
        'Tab',
      ].includes(e.key)
    )
      return
    // Only handle printable characters and backspace
    if (e.key.length !== 1 && e.key !== 'Backspace') return
    // Skip modifier combos (e.g. Ctrl+C, Cmd+A)
    if (e.ctrlKey || e.metaKey || e.altKey) return
    // Prevent default (e.g. Space firing a click on a button trigger, which would close the dropdown)
    e.preventDefault()
    // Don't open the dropdown for Backspace when it's already closed
    if (e.key !== 'Backspace') {
      if (this.menuIsClosed()) {
        this._pendingSearchKey = e.key
        this.openMenu(e)
        return
      }
    }
    if (this.menuIsClosed()) return

    const menu = this.activeMenu
    if (!menu) return
    this.dispatch('menu:searchKeypress', {
      prefix: false,
      target: menu,
      detail: { key: e.key },
    })
  }

  closeIfDropdownLostFocus(e) {
    const activeTarget = e.relatedTarget
    if (
      !activeTarget || // If we don't have a related target, let's not close the menu
      (activeTarget.readOnly &&
        activeTarget.tagName == 'TEXTAREA' &&
        activeTarget.parentNode == document.body) || // Special clippy handling - it creates an intermediate textarea and steals focus briefly on click
      this.element.contains(activeTarget) || // If the related target is inside the dropdown, let's not close the menu
      this.__content?.contains(activeTarget) || // If focus moved into the Tippy popup (which lives in document.body, outside this.element), don't close
      this.#insideChildPopper(activeTarget) || // If focus moved into a popup opened from inside this dropdown (e.g. an emblem picker), don't close
      activeTarget.contains(this.element) // If focus moved to an ancestor of the dropdown (e.g. Bootstrap modal's enforceFocus moves focus to the .modal element), don't close
    )
      return
    this.closeMenu()
  }

  openMenu(e) {
    if (e?.target?.closest('[data-prevent-dropdown-open=true]')) return
    // Don't let a focusin fire right after a manual close re-open the menu
    if (e?.type === 'focusin' && this._suppressFocusOpenUntil > Date.now())
      return

    // A lazy dropdown builds its content + tippy instance on first open. That work is
    // synchronous and can take a noticeable moment for big menus (e.g. an object's full
    // actions list), during which nothing tells the user their click registered. Paint a
    // loading spinner on the trigger first, then run the heavy setup on the next frame so
    // the spinner actually shows. Subsequent opens aren't lazy and skip straight through.
    if (this.hasLazyContentTarget) {
      if (this._lazyOpening) return
      this._lazyOpening = true
      const generation = this._lazyOpenGeneration || 0
      this.buttonTarget.classList.add('is-dropdown-loading')
      const proceed = () =>
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            if (
              !this.element.isConnected ||
              generation !== (this._lazyOpenGeneration || 0)
            )
              return
            this._lazyOpening = false
            try {
              this.setupIfLazy()
            } finally {
              this.buttonTarget.classList.remove('is-dropdown-loading')
            }
            this.openMenu(e)
          })
        )
      // A remote menu that hasn't arrived yet: keep the spinner until it has.
      if (this.lazyContentTarget.dataset?.contentUrl) {
        this.loadRemoteContent().then((loaded) => {
          if (loaded) return proceed()
          this._lazyOpening = false
          this.buttonTarget.classList.remove('is-dropdown-loading')
        })
      } else {
        proceed()
      }
      return
    }

    if (!this.menuIsClosed()) return

    this._needsAutoActivate = true
    this.updateContentMinWidth()
    this.content.tippy.show()
    this._openedAt = performance.now()
    // A pane can replace the menu while the outer dropdown stays connected.
    // Observe the menu receiving this open, not the one from first hydration.
    const menu = this.activeMenu
    if (this._hydratedLazyMenu && menu && menu !== this._lazyMenu)
      this.observeLazyMenu(menu)
    // Popper positions the popover on the next animation frame, but the focus / menu
    // auto-activate below run synchronously. With the 'absolute' strategy the popover's
    // pre-positioned origin is the document top, so those would scroll the viewport up to
    // it before it settles by the trigger. Force a synchronous reposition first. Only the
    // 'absolute' strategy needs this, so the default-'fixed' dropdowns are untouched.
    if (this.strategyValue === 'absolute')
      this.content.tippy.popperInstance?.forceUpdate()
    enter(this.__content.target)
    this.growFromTrigger()
    this.buttonTarget.setAttribute('aria-expanded', 'true')
    this.dispatch('shown')
    registerEscLayer('dropdown', this.#escLayerClose)
    if (!this._lazyMenu || this._lazyMenuReady) this.flushPendingMenuAction()

    if (
      e?.target?.tagName !== 'INPUT' &&
      (this.autoFocusOnMobileValue || window.innerWidth > 640)
    ) {
      ;(this.activeMenu || this.content)
        ?.querySelector(
          "input[type='url'], input[type='text'], input[type='search']"
        )
        // preventScroll: the popover is shown synchronously but Popper positions it on the
        // next frame, so at focus time it's still at its initial offset (the document
        // origin for the 'absolute' strategy). Without this, focusing the input scrolls the
        // viewport up to that origin before the popover settles next to its trigger. Only
        // the 'absolute' strategy needs it; 'fixed' keeps the default (preventScroll: false).
        ?.focus({ preventScroll: this.strategyValue === 'absolute' })
    }
  }

  closeMenu(e) {
    if (this.menuIsClosed()) {
      this.cancelPendingLazyOpen()
      return
    }

    // Don't close if clicked on a clippy icon
    if (e?.target?.closest('[data-clipboard-text], [data-clipboard-url]'))
      return

    // Don't close if clicked on a text/search input
    if (
      e?.target?.closest('input[type=text], input[type=search]') &&
      e?.type == 'click'
    )
      return

    // Don't close if clicked on an item with a data-prevent-dropdown-close in its ancestor chain, e.g. a switch
    // item. The items live in the Tippy popper, which sits outside this.element when appended to the body.
    if (e?.type == 'click') {
      const preventDropdownCloseEl = e?.target?.closest(
        '[data-prevent-dropdown-close]'
      )
      if (
        preventDropdownCloseEl?.matches('[data-prevent-dropdown-close=true]') &&
        (this.element.contains(preventDropdownCloseEl) ||
          this.__content?.contains(preventDropdownCloseEl))
      )
        return
    }

    // Nested submenu trigger: keep parent open so the flyout can open (esp. on touch,
    // where open_on_focus_or_hover has no hover and used to just close this menu).
    // Menu items live in the Tippy popper (this.__content), not this.element, after setup.
    if (e?.type === 'click') {
      const nestedDropdown = e.target?.closest?.('.js-menu-item-dropdown')
      if (
        nestedDropdown &&
        nestedDropdown !== this.element &&
        this.__content?.contains(nestedDropdown)
      )
        return
    }

    this._pendingMenuEvent = null
    this._pendingSearchKey = null
    this._needsAutoActivate = false
    this.dispatchEventToActiveMenu('menu:deactivateActiveItem')
    const closingContent = this.__content
    // Focus left inside it (Escape, a pick) would drop to <body>, so it goes back to the trigger.
    // And it leaves the tab order now, not after the hide transition: it can sit right after the
    // trigger.
    if (closingContent.contains(document.activeElement)) this.#focusTrigger()
    closingContent.inert = true
    leave(closingContent.target).then(() => {
      if (this.__content !== closingContent) return
      reconcileHiddenAfterLeaveSettles(closingContent, this.menuIsClosed())
    })
    this.content.tippy.hide()
    this.buttonTarget.setAttribute('aria-expanded', 'false')
    this.dispatch('hidden')
    unregisterEscLayer('dropdown', this.#escLayerClose)

    if (
      this.element.classList.contains('js-menu-item-dropdown') &&
      this.parentDropdown
    ) {
      // In a standard nested dropdown case (i.e where a menu item in a dropdown has this dropdown, let's also close the parent dropdown when child dropdown closes on a click event - i.e you picked something on the child dropdown)
      if (e?.type == 'click') {
        this.dispatch('close', { target: this.parentDropdown })
      }

      // Move focus back to the parent dropdown trigger
      const trigger = this.parentDropdown.querySelector(
        '[data-dropdown-target="button"]'
      )
      const triggerFocusable = trigger?.querySelector('button, a, [tabindex]')
      triggerFocusable?.focus()
    }
  }

  get canHover() {
    return window.matchMedia('(hover: hover)').matches
  }

  openMenuOnMouseEnter(e) {
    if (!this.canHover) return
    if (this.menuIsClosed()) {
      this.openMenu(e)
    }
    if (this.closeMenuOnMouseLeaveTimeout) {
      clearTimeout(this.closeMenuOnMouseLeaveTimeout)
    }
  }

  closeMenuOnMouseLeave(e) {
    if (!this.canHover) return
    this.closeMenuOnMouseLeaveTimeout = setTimeout(() => {
      this.closeMenu(e)
      this.closeMenuOnMouseLeaveTimeout = null
    }, 150)
  }

  // open_on_focus_or_hover: desktop uses hover; touch has no hover so click must open
  // the nested flyout. Stop propagation so the parent menu's close_on_click doesn't
  // dismiss everything before the submenu appears.
  toggleMenuOnTouch(e) {
    if (this.canHover) return
    e?.preventDefault?.()
    e?.stopPropagation?.()
    this.toggleMenu(e)
  }

  toggleMenu(e) {
    if (this.menuIsClosed()) {
      this.openMenu(e)
    } else {
      this.closeMenu(e)
      this._suppressFocusOpenUntil = Date.now() + 200
    }
  }

  reposition() {
    this.content.tippy.setProps({})
  }

  setTippyProps(e) {
    const props = e.detail?.tippyProps || e.params?.tippyProps
    this.content.tippy.setProps(props)
  }
  /* ====== End Public API ====== */

  // Closes the menu on a click anywhere else on the page. Focus loss alone (closeIfDropdownLostFocus) misses clicks
  // on things that can't take focus, like an automation step card.
  clickOutside(e) {
    // Not built yet (lazy) means not open; checking first keeps this from building every menu on the page
    if (!this.__content || this.menuIsClosed()) return

    // The click that opened the menu, e.g. one on a field's padding that opens its time zone menu (form--field#focusControl)
    if (e.timeStamp < this._openedAt) return

    // Clicked the trigger (toggleMenu handles that) or something else inside the dropdown
    if (this.element.contains(e.target)) return

    // Clicked inside the Tippy popup (which lives in document.body, outside this.element)
    if (this.__content?.contains(e.target)) return

    // Selected text in a field inside by dragging past the menu's edge: the browser clicks an ancestor of both
    const field = document.activeElement
    if (
      field?.selectionStart !== field?.selectionEnd &&
      (this.element.contains(field) || this.__content.contains(field))
    )
      return

    // Clicked inside another popup (a tooltip, a nested flyout), a modal or a bottom sheet the menu opened. One that
    // holds this dropdown doesn't count: a click elsewhere in it is still outside.
    const popup = e.target.closest?.(
      '[data-tippy-root], .modal, .tw-modal-containers, dialog, [data-controller~="bottom-sheet"]'
    )
    if (popup && !popup.contains(this.element)) return

    // Nested submenu tippies are also portaled to body — clicking a project in a
    // "Move to project" flyout must not count as outside the parent menu.
    if (this.#clickInsideNestedDropdownPopper(e.target)) return

    // Same for any other Tippy popup opened from inside this dropdown, e.g. the emblem picker
    // in an object picker's create form.
    if (this.#insideChildPopper(e.target)) return

    // Element not connected to the DOM. Could be a thing inside the dropdown
    if (!e.target.closest('html')) return

    this.closeMenu()
  }

  #insideChildPopper(target) {
    const reference = target?.closest?.('[data-tippy-root]')?._tippy?.reference
    return !!reference && !!this.__content?.contains(reference)
  }

  #clickInsideNestedDropdownPopper(target) {
    if (!target) return false
    // Nested .js-menu-item-dropdown nodes are inside the portaled popper (this.__content),
    // not this.element, after Tippy setup.
    for (const el of this.__content?.querySelectorAll(
      '.js-menu-item-dropdown'
    ) ?? []) {
      const ctrl = this.application.getControllerForElementAndIdentifier(
        el,
        'dropdown'
      )
      if (ctrl?.__content?.contains(target)) return true
    }
    return false
  }

  // Dispatches the given eventName to the currently active menu
  // When `e` (a Event) is given, it will only dispatch if the event didn't come from inside the menu.
  dispatchEventToActiveMenu(eventName, e) {
    const menu = this.activeMenu
    if (!menu) return
    this.dispatch(eventName, { prefix: false, target: menu })
  }

  menuIsClosed() {
    // A lazy dropdown hasn't built its tippy instance yet, so it's definitionally closed.
    // Returning early also keeps a closed/open check (e.g. from toggleMenu) from triggering
    // the heavy lazy setup via the `content` getter before we've shown a loading state.
    if (this.hasLazyContentTarget) return true
    const tippy = this.content?.tippy
    return !tippy?.state.isMounted || !tippy.state.isVisible
  }

  updateContentMinWidth() {
    if (this.minWidthValue === 'full') {
      const minWidth =
        (this.referenceEl || this.buttonTarget).offsetWidth + 'px'
      this.content.style.minWidth = minWidth
      this.content.style.setProperty('--dropdown-content-min-width', minWidth)
    }
  }

  // TODO: handle cases when there are multiple menus in a pane
  get activeMenu() {
    return this.hasMenuTarget
      ? this.menuTargets[this.menuTargets.length - 1]
      : null
  }

  get content() {
    if (!this.__content) this.setupIfLazy()
    return this.__content
  }

  get parentDropdown() {
    return this.element.parentElement.closest('.js-dropdown')
  }
}
